import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import useFetch from "hooks/useFetch";
import useLogout from "hooks/useLogout";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeadlessSimpleModal from "./Modal/HeadlessSimpleModal";
import useUserData from "hooks/useUserData";

/**
 * The DataFetcher component can be used for multiple fetches. It does, however require a certain way of usage within a component:
 * 1) The fetchData is passed in as a useState hook variable, to ensure rendering on the DataFetcher for each change
 * 2a) If fetchData is not initialised, using another "step" type variable is required to control the flow of each request
 * 2b) If fetchData is initialised, it can be updated through setData as a useCallback, or as a useEffect hook where setData is a useState function
 */

export const DataFetcher = ({ fetchData, setData, setError = null, children }) => {
	const { executing, execute } = useFetch();
	const [hasError, setHasError] = useState(false);
	const [forbiddenError, setForbiddenError] = useState(null);
	const location = useLocation();
	const navigate = useNavigate();
	const { logout } = useLogout(navigate);
	const { username } = useUserData();

	// Effect that handles the error logic; other errors (301 - 402, 404+) are not yet handled.
	const handleError = useCallback((error) => {
		if (error) {
			let allowErrorProcessing = true;
			if (error.Status === 403 || error.Status === 401) {
				const forbiddenMessageMap = {
					"ERR_AUTHORIZATION_MISSING": "",
					"ERR_AUTHORIZATION_SCHEME": "",
					"ERR_SESSION_NO_IDENTITY": "",
					"ERR_SESSION_NOT_LOGGED_IN": "",
					"ERR_SESSION_LOGIN_NEW_LOCATION": "",
					"ERR_SESSION_TIMEOUT": " due to a long period of inactivity",
					"ERR_LOGIN_TIMEOUT": " due to a long period of inactivity",
					"ERR_UNAUTHORIZED_LOGOOUT": ""
				};
				const isMappedMessage = error.Message in forbiddenMessageMap;
				if (isMappedMessage || error.Status === 401) {
					const prefix = "You will be redirected to the login page";
					const suffix = ". You will be returned to this page after logging in.";
					const middle = isMappedMessage ? forbiddenMessageMap[error.Message] : "";
					setForbiddenError({
						Status: error.Status,
						Message: (<div>{prefix + middle + suffix}</div>)
					});
					allowErrorProcessing = false;
				}
			}
			if (allowErrorProcessing && setError) {
				setError(error);
			}
		}
	}, [setError]);

	// A warning for missing "fetchData" object occurs; objects in useEffect dependencies cause infinite loops.
	useEffect(() => {
		if (fetchData && fetchData.url && !hasError) {
			setForbiddenError(null);
			execute(fetchData.url, fetchData.options, (fetchedData, error) => {
				const context = fetchData.context;
				const contextHasValue = typeof context === "string" && context.trim() !== "";

				if (!error) {
					setHasError(false);

					const token = fetchData.token;
					const tokenHasValue = typeof token === "number" && token >= 1;

					if (tokenHasValue && contextHasValue) {
						setData(fetchedData, token, context);
					}
					else if (tokenHasValue) {
						setData(fetchedData, token, null);
					}
					else if (contextHasValue) {
						setData(fetchedData, null, context);
					}
					else {
						setData(fetchedData, null, null);
					}
				}
				else {
					setHasError(true);
					if (contextHasValue) {
						handleError(error, context);
					}
					else {
						handleError(error, null);
					}
				}
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchData.url, fetchData.options, fetchData.token, fetchData.context, execute, handleError]); // fetchData, setData is an output sink, not a trigger
	
	const handleCloseModal = () => {
		if (forbiddenError) {
			const key = "ucs-lau-" + (username ? window.btoa(username) : "anon");
			localStorage.setItem(key, location.pathname + location.search + location.hash);
			logout();
		}
	};

	const render = () => {
		if (executing) {
			return <LoadingSpinner />;
		}
		if (forbiddenError) {
			return <HeadlessSimpleModal showModal={true} title={""} content={forbiddenError.Message} closeModal={handleCloseModal} />;
		}
		else {
			return children;
		}
	}

	return (<>{render()}</>);
};
